import Bugsnag from '@bugsnag/js'
import { ProductVariantSneakersResponse } from 'productTemplate/types/ProductVariant'
import { resolveVariantPrice } from 'server/utils/productTemplate/productVariantUtils'
import { Price, PriceLocalized } from 'shared/types'
import { NewProductSizeOption } from 'shared/types/NewProductSizeOption'
import { ProductToggleOption } from 'shared/types/ProductToggleOptions'

const createProductToggleOption = (
  isAvailable: boolean,
  price: Price | null,
): ProductToggleOption => ({
  price,
  isAvailable,
})

const getSizeDisplay = (sizeOption: { presentation: string; value: number }) => {
  if (sizeOption.presentation) {
    return sizeOption.presentation.toUpperCase()
  }
  throw new Error('No presentation on size option')
}
const getLowestPriceOption = (
  priceOption: PriceLocalized,
  instantShipOption: PriceLocalized,
): ProductToggleOption => {
  const isLowestPriceOptionAvailable = priceOption.amount !== instantShipOption.amount
  const price = resolveVariantPrice(priceOption)
  const productToggleOption = createProductToggleOption(isLowestPriceOptionAvailable, price)

  return productToggleOption
}

const getInstantShipOption = (instantShipOption: PriceLocalized): ProductToggleOption => {
  const isAvailable = !!instantShipOption.amountUsdCents
  const price = isAvailable ? resolveVariantPrice(instantShipOption) : null
  const productToggleOption = createProductToggleOption(isAvailable, price)

  return productToggleOption
}

export const resolveNewSizes = (
  newSizes: ProductVariantSneakersResponse[],
  productTemplateId: string,
): NewProductSizeOption[] => {
  return newSizes.map((newSize) => {
    const lowestPriceOption = getLowestPriceOption(
      newSize.lowestPriceCents,
      newSize.instantShipLowestPriceCents,
    )
    const instantShipPriceOption = getInstantShipOption(newSize.instantShipLowestPriceCents)
    let display = ''
    try {
      display = getSizeDisplay(newSize.sizeOption)
    } catch (e) {
      Bugsnag.notify(
        new Error(
          `No presentation value for sizeOption ${newSize.size} for product template with id: ${productTemplateId}`,
        ),
      )
    }
    return {
      productTemplateId,
      size: {
        value: newSize.sizeOption.value,
        display,
      },
      lowestPriceOption,
      boxCondition: newSize.boxCondition,
      shoeCondition: newSize.shoeCondition,
      instantShipPriceOption,
      isInstantShip: !lowestPriceOption.isAvailable,
    }
  })
}

export const resolveNewSizesInstantShipOnly = (
  newSizes: ProductVariantSneakersResponse[],
  productTemplateId: string,
): NewProductSizeOption[] => {
  const instantShipOnlySizes = newSizes.filter(
    (newSize) =>
      !!(newSize.instantShipLowestPriceCents && newSize.instantShipLowestPriceCents.amountUsdCents),
  )

  return instantShipOnlySizes.map((newSize) => {
    const instantShipPriceOption = getInstantShipOption(newSize.instantShipLowestPriceCents)
    let display = ''
    try {
      display = getSizeDisplay(newSize.sizeOption)
    } catch (e) {
      Bugsnag.notify(
        new Error(
          `No presentation value for sizeOption ${newSize.size} for product template with id: ${productTemplateId}`,
        ),
      )
    }
    return {
      productTemplateId,
      size: {
        value: newSize.sizeOption.value,
        display,
      },
      lowestPriceOption: {
        price: instantShipPriceOption.price,
        isAvailable: false,
      },
      boxCondition: newSize.boxCondition,
      shoeCondition: newSize.shoeCondition,
      instantShipPriceOption,
      isInstantShip: true,
    }
  })
}
